import { useState, useEffect, useCallback } from "react";
import { getAgentDetailsByAgentId, getAgentsByGroupId, getAllAgents } from "../api/userApi";
import { useUser } from "../context/userContext";

export function UseFetchDownlineAgents(shouldFetch = true) {
    const { user } = useUser();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(shouldFetch);
    const [error, setError] = useState(null);

    const fetchDownlineAgents = useCallback(async (signal) => {
        if (!shouldFetch) {
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const response = await getAllAgents({ signal });
            const agentArray = Array.isArray(response)
                ? response
                : (
                    response && Array.isArray(response.data)
                        ? response.data
                        : []
                );
            const filteredAgent = agentArray.filter(agent => String(agent.uplineId) === String(user.userId));
            setData(filteredAgent);
        }
        catch (err) {
            if (err.name !== "AbortError") {
                setError(err);
            }
        }
        finally {
            setLoading(false);
        }
    }, [shouldFetch, user.userId]);

    // call once on mount, with an AbortController
    useEffect(() => {
        if (!shouldFetch) {
            setLoading(false);
            return;
        }
        const ac = new AbortController();
        fetchDownlineAgents(ac.signal);
        return () => ac.abort();
    }, [fetchDownlineAgents, shouldFetch]);

    // refresh exposed to consumers
    const refresh = useCallback(() => {
        if (shouldFetch) {
            fetchDownlineAgents();
        }
    }, [fetchDownlineAgents, shouldFetch]);

    return { data, loading, error, refresh };
}

export function UseFetchGroupAgents(shouldFetch = true) {
    const { user } = useUser();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(shouldFetch);
    const [error, setError] = useState(null);

    // memoized fetch — accepts an optional AbortSignal
    const fetchGroupAgents = useCallback(async (signal) => {
        if (!shouldFetch) {
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const agentDetails = await getAgentDetailsByAgentId(user.userId, { signal });
            const response = await getAgentsByGroupId(agentDetails.data.groupId, { signal });
            const agentArray = Array.isArray(response)
                ? response
                : (
                    response && Array.isArray(response.data)
                        ? response.data
                        : []
                );
            setData(agentArray);
        }
        catch (err) {
            if (err.name !== "AbortError") {
                setError(err);
            }
        }
        finally {
            setLoading(false);
        }
    }, [shouldFetch, user.userId]);

    // call once on mount, with an AbortController
    useEffect(() => {
        if (!shouldFetch) {
            setLoading(false);
            return;
        }
        const ac = new AbortController();
        fetchGroupAgents(ac.signal);
        return () => ac.abort();
    }, [fetchGroupAgents, shouldFetch]);

    // refresh exposed to consumers
    const refresh = useCallback(() => {
        if (shouldFetch) {
            fetchGroupAgents();
        }
    }, [fetchGroupAgents, shouldFetch]);

    return { data, loading, error, refresh };
}